import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getProductByPriceId } from '../../stripe-config';
import { CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

interface SubscriptionData {
  subscription_status: string;
  price_id: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
}

export function SubscriptionStatus() {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_user_subscriptions')
        .select('subscription_status, price_id, current_period_end, cancel_at_period_end')
        .maybeSingle();

      if (error) {
        setError('Failed to fetch subscription data');
        console.error('Subscription fetch error:', error);
        return;
      }

      setSubscription(data);
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!subscription || subscription.subscription_status === 'not_started') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-500" />
          <p className="text-sm text-gray-600">No active subscription</p>
        </div>
      </div>
    );
  }

  const product = subscription.price_id ? getProductByPriceId(subscription.price_id) : null;
  const isActive = subscription.subscription_status === 'active';
  const isCanceled = subscription.subscription_status === 'canceled';
  const willCancel = subscription.cancel_at_period_end;

  const getStatusIcon = () => {
    if (isActive && !willCancel) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (willCancel) return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    if (isCanceled) return <XCircle className="h-5 w-5 text-red-500" />;
    return <Clock className="h-5 w-5 text-gray-500" />;
  };

  const getStatusText = () => {
    if (isActive && !willCancel) return 'Active';
    if (willCancel) return 'Canceling at period end';
    if (isCanceled) return 'Canceled';
    return subscription.subscription_status.replace('_', ' ');
  };

  const getStatusColor = () => {
    if (isActive && !willCancel) return 'text-green-600';
    if (willCancel) return 'text-yellow-600';
    if (isCanceled) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Status</h3>
      
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>

        {product && (
          <div>
            <p className="text-sm text-gray-600">Plan:</p>
            <p className="font-medium text-gray-900">{product.name}</p>
          </div>
        )}

        {subscription.current_period_end && (
          <div>
            <p className="text-sm text-gray-600">
              {willCancel ? 'Ends on:' : 'Next billing:'}
            </p>
            <p className="font-medium text-gray-900">
              {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}